const modalOptions = new WeakMap();
const eventHandlers = new WeakMap();

function bootstrap() {
  if (!window.bootstrap?.Modal) {
    throw new Error("Bootstrap 5 runtime is not available");
  }
  return window.bootstrap;
}

function elements(target) {
  if (typeof target === "string") {
    return [...document.querySelectorAll(target)];
  }
  if (target instanceof Element) {
    return [target];
  }
  return [...(target || [])].filter((element) => element instanceof Element);
}

function eventDescriptor(eventName) {
  if (eventName.startsWith(".")) {
    return { type: null, namespace: eventName.slice(1) };
  }

  const parts = eventName.split(".");
  return {
    type: parts.slice(0, 3).join("."),
    namespace: parts.slice(3).join("."),
  };
}

function modalInstance(element) {
  const { Modal } = bootstrap();
  return Modal.getOrCreateInstance(element, modalOptions.get(element) || {});
}

export function appModal(target, commandOrOptions) {
  const matched = elements(target);
  if (commandOrOptions && typeof commandOrOptions === "object") {
    for (const element of matched) {
      modalOptions.set(element, commandOrOptions);
      bootstrap().Modal.getInstance(element)?.dispose();
      const instance = modalInstance(element);
      if (commandOrOptions.show !== false) {
        instance.show();
      }
    }
    return matched;
  }

  for (const element of matched) {
    const instance = modalInstance(element);
    if (commandOrOptions === "toggle") {
      instance.toggle();
    } else if (commandOrOptions === "dispose") {
      instance.dispose();
    } else {
      instance[commandOrOptions]();
    }
  }
  return matched;
}

export function hideOpenAppModals() {
  return appModal(".modal.show", "hide");
}

export function configureAppModal(target, options) {
  const matched = elements(target);
  for (const element of matched) {
    modalOptions.set(element, options);
    const existing = bootstrap().Modal.getInstance(element);
    if (existing && !element.classList.contains("show")) {
      existing.dispose();
      modalInstance(element);
    }
  }
  return matched;
}

export function onAppInteraction(target, eventName, handler) {
  const descriptor = eventDescriptor(eventName);
  if (!descriptor.type) {
    throw new Error(`Bootstrap event type is required: ${eventName}`);
  }

  for (const element of elements(target)) {
    const records = eventHandlers.get(element) || [];
    element.addEventListener(descriptor.type, handler);
    records.push({ ...descriptor, handler });
    eventHandlers.set(element, records);
  }
}

export function offAppInteraction(target, eventName, handler) {
  const descriptor = eventDescriptor(eventName);
  for (const element of elements(target)) {
    const records = eventHandlers.get(element) || [];
    const retained = [];
    for (const record of records) {
      const matchesType = !descriptor.type || record.type === descriptor.type;
      const matchesNamespace = !descriptor.namespace || record.namespace === descriptor.namespace;
      const matchesHandler = !handler || record.handler === handler;
      if (matchesType && matchesNamespace && matchesHandler) {
        element.removeEventListener(record.type, record.handler);
      } else {
        retained.push(record);
      }
    }
    eventHandlers.set(element, retained);
  }
}

export function initializeAppTooltips(selector = '[data-bs-toggle="tooltip"]') {
  const { Tooltip } = bootstrap();
  return elements(selector).map((element) =>
    Tooltip.getOrCreateInstance(element, {
      html: false,
      sanitize: true,
    }),
  );
}
